/**
 * API Client Service
 * 
 * Gold-standard HTTP client for all API requests with:
 * - Type-safe discriminated unions (ApiResult<T>)
 * - Automatic error handling with toast integration
 * - Request deduplication (1s window)
 * - Retry logic with exponential backoff
 * - Automatic token refresh on 401 errors
 * - Timeout handling
 * - Tenant context (URL params + headers)
 * - Monitoring integration
 * - Full request pipeline tracing
 * 
 * @example
 * ```typescript
 * import { apiClient, type ApiResult } from '~/lib/apiClient';
 * 
 * // GET request
 * const result = await apiClient.get<User>('/users/1', {
 *   tenantHandle: 'drivingrangecollective',
 *   clubId: 2,
 * });
 * 
 * if (result.success) {
 *   console.log(result.payload); // User
 * } else {
 *   console.error(result.message); // Error message
 * }
 * 
 * // POST request with body
 * const bookingResult = await apiClient.post<Booking>('/bookings', {
 *   date: '2024-12-15',
 *   timeSlot: '10:00',
 * }, {
 *   tenantHandle: 'drivingrangecollective',
 *   clubId: 2,
 * });
 * ```
 */

import { API_CONFIG, RETRY_CONFIG, ERROR_CODES, IS_PRODUCTION } from './constants';
import { ApiError, showErrorToast } from './errors';
import type { ApiResult, RequestOptions } from './types';
import { buildHeaders, buildVersionedUrl } from './utils/headers';
import { createTimeoutController, isTimeoutError as isTimeoutErrorUtil } from './utils/timeout';
import { deduplicateRequest, generateRequestKey } from './utils/requestDedup';
import { withRetry, isRetryableStatus } from './utils/retryLogic';
import { isLegacyResponse, transformLegacyResponse } from './utils/responseTransform';
import { logErrorToMonitoring, logRetryToMonitoring, extractMonitoringContext } from './utils/monitoring';
import { getTenantHeaders } from './utils/getTenantHeaders';

// ============================================================================
// Request Pipeline
// ============================================================================

/**
 * Execute HTTP request with full pipeline
 * 
 * Pipeline:
 * 1. URL construction (version + tenant context)
 * 2. Header preparation (auth + client info)
 * 3. Request deduplication check
 * 4. Timeout controller creation
 * 5. Fetch with retry logic
 * 6. Response parsing & 401 handling (automatic token refresh)
 * 7. Response transformation (legacy → new)
 * 8. Error handling (toast + monitoring)
 * 9. Cleanup (abort controller, pending requests)
 * 
 * @param method - HTTP method (GET, POST, etc.)
 * @param endpoint - API endpoint (e.g., /bookings)
 * @param body - Request body (optional)
 * @param options - Request options (timeout, headers, tenant context, etc.)
 * @returns ApiResult<T> discriminated union (success or error)
 * 
 * @internal - Use get/post/put/patch/delete methods instead
 */
async function request<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  const startTime = Date.now();
  const httpMethod = method.toUpperCase();
  let attempts = 0;
  const maxRetries = options?.retries ?? RETRY_CONFIG.MAX_RETRIES;

  // Build request key for deduplication
  const requestKey = generateRequestKey(
    httpMethod,
    buildVersionedUrl(endpoint, options),
    body ? JSON.stringify(body) : undefined
  );

  // Monitoring context for all logging
  const monitoringContext = extractMonitoringContext(options, endpoint, httpMethod);

  try {
    // Define the core request logic (used with or without deduplication)
    const executeRequest = async () => {
      return await withRetry(
        async () => {
          attempts++;

          // Log retry attempts to monitoring
          if (attempts > 1) {
            logRetryToMonitoring(monitoringContext, attempts, new Error('Retry attempt'));
          }

          // 1. URL Construction
          const url = buildVersionedUrl(endpoint, options);

          // 2. Inject Tenant Context (from appRuntime's URL params, NOT from
          //    TenantProvider — that provider carries branding for rendering and
          //    has no part in request headers).
          const tenantContext = getTenantHeaders(options);
          const optionsWithTenant: RequestOptions = {
            ...options,
            tenantHandle: tenantContext?.tenantHandle ?? options?.tenantHandle,
            clubId: tenantContext?.clubId ?? options?.clubId,
          };

          // 3. Header Preparation
          const headers = await buildHeaders(optionsWithTenant);

          // 4. Timeout Controller
          const timeout = options?.timeout ?? API_CONFIG.DEFAULT_TIMEOUT_MS;
          const { controller, cleanup: cleanupTimeout } = createTimeoutController(timeout);

          try {
            // 5. Fetch Request
            const response = await fetch(url, {
              method: httpMethod,
              headers,
              body: body ? JSON.stringify(body) : undefined,
              signal: controller.signal,
            });

            // 6. Parse Response
            const responseText = await response.text();
            let json: unknown;
            
            try {
              json = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
              // JSON parse error - server sent invalid JSON (likely HTML error page)
              // Retry behavior: Only retries if HTTP status is retryable (5xx/408; never 429) -
              // a 503 with HTML may recover, but a 200 with bad JSON won't
              throw new ApiError(
                'Server returned invalid JSON response',
                ERROR_CODES.PARSE_ERROR,
                {
                  userMessage: 'The server returned an invalid response. Please try again later.',
                  status: response.status,
                  details: {
                    // Include first 500 chars for debugging (avoid huge logs)
                    responseText: responseText.slice(0, 500),
                    parseError: parseError instanceof Error ? parseError.message : 'Unknown parse error',
                    httpStatus: response.status,
                  }
                }
              );
            }

            // 6.5. Handle 401 Unauthorized - Reactive Token Refresh
            if (response.status === 401 && !options?.skipAuthRefresh) {
              cleanupTimeout(); // Clean up this request's timeout
              
              if (!IS_PRODUCTION) {
                console.log('[ApiClient] 🔄 401 Unauthorized - attempting token refresh...');
              }

              try {
                // Dynamically import to avoid circular dependency
                const { refreshAccessToken } = await import('~/services/auth/api/tokenRefresh');
                const { hasValidAccessToken } = await import('~/services/auth/storage/tokenStorage');
                
                // Check if we have a valid access token before attempting refresh
                // This prevents unnecessary refresh attempts for public endpoints that returned 401
                if (hasValidAccessToken()) {
                  // Token is still valid, no need to refresh
                  // Fall through to handle 401 error
                } else {
                  // No valid token - attempt refresh
                  try {
                    await refreshAccessToken();
                    
                    if (!IS_PRODUCTION) {
                      console.log('[ApiClient] ✅ Token refreshed, retrying request...');
                    }
                    
                    // Retry the original request with new token
                    // CRITICAL: Set skipDedup to prevent deadlock from hitting the in-flight cache
                    return await request<T>(method, endpoint, body, {
                      ...options,
                      skipAuthRefresh: true, // Prevent infinite loop
                      skipDedup: true, // Bypass deduplication to avoid deadlock
                    });
                  } catch (refreshError) {
                    // Token refresh failed (rejected → tokens cleared; transient → kept)
                    if (!IS_PRODUCTION) {
                      console.log('[ApiClient] Token refresh failed', refreshError);
                    }
                    // Fall through to return 401 error
                  }
                }
              } catch (error) {
                console.error('[ApiClient] ❌ Error during token refresh logic:', error);
                // Fall through to return 401 error
              }
            }

            // 7. Handle Empty Responses (204 No Content)
            if (response.status === 204) {
              const result: ApiResult<T> = {
                httpOk: true,
                httpStatus: response.status,
                success: true,
                payload: null as unknown as T, // 204 = no content, caller should use void or null type
                message: 'Request successful',
              };

              return result;
            }

            // 8. Response Transformation
            // Detect and transform legacy formats to new format
            const apiResponse = isLegacyResponse(json)
              ? transformLegacyResponse<T>(json)
              : (json as import('./types').ApiResponse<T>);

            // 9. Build Result with HTTP Metadata
            // Structure: { httpOk, httpStatus, ...apiResponseData }
            // Check apiResponse.success to properly type the result
            if (apiResponse.success) {
              const result: ApiResult<T> = {
                httpOk: true,
                httpStatus: response.status,
                ...apiResponse,
              };

              return result;
            } else {
              // ACCOUNT_SETUP_REQUIRED: the API refuses to let a
              // pre-enrollment account hold a session, on ANY request. Clear the
              // dead session and let AuthProvider route the member to sign-in →
              // account setup (dynamic imports mirror the refresh path above —
              // apiClient must not statically depend on the auth service).
              if (response.status === 403 && apiResponse.error?.code === 'ACCOUNT_SETUP_REQUIRED') {
                try {
                  const { authEvents } = await import('~/services/auth/events');
                  authEvents.emit('setupRequired', null);
                } catch (setupErr) {
                  console.error('[ApiClient] Failed to dispatch setup-required handling:', setupErr);
                }
              }

              const result: ApiResult<T> = {
                httpOk: false,
                httpStatus: response.status,
                ...apiResponse,
              };

              const duration = Date.now() - startTime;

              if (!IS_PRODUCTION) {
                console.log(
                  `[ApiClient] ⚠️  API Error (${response.status})`,
                  `\n  Duration: ${duration}ms`,
                  `\n  Result:`, result
                );
              }

              return result;
            }
          } catch (error) {
            // Handle fetch errors (network, timeout, parse errors)
            if (isTimeoutErrorUtil(error)) {
              // Timeout errors are always retryable based on error code
              throw new ApiError(
                'Request timed out',
                ERROR_CODES.TIMEOUT_ERROR,
                {
                  userMessage: 'The request took too long. Please try again.',
                }
              );
            }

            // Re-throw ApiErrors as-is (preserves code/status/details)
            // This includes PARSE_ERROR, and any other ApiError we've already constructed
            if (error instanceof ApiError) {
              throw error;
            }

            // Network error - always retryable
            // Only wrap unknown errors or fetch TypeErrors as NETWORK_ERROR
            throw new ApiError(
              error instanceof Error ? error.message : 'Network error',
              ERROR_CODES.NETWORK_ERROR,
              {
                userMessage: 'Unable to connect. Please check your internet connection.',
              }
            );
          } finally {
            cleanupTimeout();
          }
        },
        maxRetries,
        (error) => {
          // Determine if error should be retried
          if (error instanceof ApiError) {
            // Check status code
            if (error.status !== undefined) {
              return isRetryableStatus(error.status);
            }
            
            // Network errors are retryable
            return error.code === ERROR_CODES.NETWORK_ERROR ||
                   error.code === ERROR_CODES.TIMEOUT_ERROR;
          }
          
          return false;
        }
      );
    };
    
    // Check if we should skip deduplication (e.g., for token refresh retries)
    if (options?.skipDedup) {
      // Skip deduplication - execute request directly
      return await executeRequest();
    } else {
      // Normal flow - deduplicate identical requests
      return await deduplicateRequest(requestKey, executeRequest);
    }
  } catch (error) {
    // Handle final error after retries exhausted
    const duration = Date.now() - startTime;

    // Convert unknown errors to ApiError
    const apiError = error instanceof ApiError
      ? error
      : new ApiError(
          error instanceof Error ? error.message : 'Unknown error',
          ERROR_CODES.INTERNAL_ERROR,
          {
            userMessage: 'An unexpected error occurred. Please try again.',
          }
        );

    // Build error result
    const result: ApiResult<T> = {
      httpOk: false,
      httpStatus: apiError.status ?? 0,
      success: false,
      message: apiError.message,
      error: {
        code: apiError.code,
        details: apiError.details,
      },
    };

    // Log error to monitoring
    logErrorToMonitoring(apiError, {
      ...monitoringContext,
      status: apiError.status,
      attempt: attempts,
      durationMs: duration,
    });

    // Show toast for network errors — reads only; mutations toast at the call site
    await showErrorToast(apiError, httpMethod, options);

    if (!IS_PRODUCTION) {
      console.error(
        `[ApiClient] ❌ Failed after ${attempts} attempt(s)`,
        `\n  Error: ${apiError.message}`,
        `\n  Code: ${apiError.code}`,
        `\n  Duration: ${duration}ms`,
        `\n  Result:`, result
      );
    }

    return result;
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Get request
 * 
 * @param endpoint - API endpoint
 * @param options - Request options
 * @returns ApiResult<T>
 * 
 * @example
 * ```typescript
 * const result = await apiClient.get<Booking>('/bookings/123');
 * if (result.success) {
 *   console.log(result.payload);
 * }
 * ```
 */
async function get<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  return request<T>('GET', endpoint, undefined, options);
}

/**
 * POST request
 * 
 * @param endpoint - API endpoint
 * @param body - Request body
 * @param options - Request options
 * @returns ApiResult<T>
 * 
 * @example
 * ```typescript
 * const result = await apiClient.post<Booking>('/bookings', {
 *   date: '2024-12-15',
 *   timeSlot: '10:00',
 * });
 * ```
 */
async function post<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  return request<T>('POST', endpoint, body, options);
}

/**
 * PUT request
 * 
 * @param endpoint - API endpoint
 * @param body - Request body
 * @param options - Request options
 * @returns ApiResult<T>
 */
async function put<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  return request<T>('PUT', endpoint, body, options);
}

/**
 * PATCH request
 * 
 * @param endpoint - API endpoint
 * @param body - Request body
 * @param options - Request options
 * @returns ApiResult<T>
 */
async function patch<T>(
  endpoint: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  return request<T>('PATCH', endpoint, body, options);
}

/**
 * DELETE request
 * 
 * @param endpoint - API endpoint
 * @param options - Request options
 * @returns ApiResult<T>
 * 
 * @example
 * ```typescript
 * // For 204 No Content responses, use void as the type parameter
 * const result = await apiClient.delete<void>('/bookings/123');
 * if (result.success) {
 *   console.log('Deleted successfully');
 * }
 * ```
 */
async function deleteRequest<T>(
  endpoint: string,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  return request<T>('DELETE', endpoint, undefined, options);
}

// ============================================================================
// Exports
// ============================================================================

/**
 * Main API client instance
 * 
 * Use this for all API requests to any endpoint.
 * Automatically handles:
 * - Authentication (token injection)
 * - Retry logic (transient failures)
 * - Request deduplication (same request within 1s)
 * - Timeout handling (30s default)
 * - Error handling (toast + monitoring)
 * - Response transformation (legacy formats)
 * - Tenant context (URL params + headers)
 * 
 * @example
 * ```typescript
 * // GET
 * const bookings = await apiClient.get<Booking[]>('/bookings');
 * 
 * // POST
 * const booking = await apiClient.post<Booking>('/bookings', {
 *   date: '2024-12-15',
 *   timeSlot: '10:00',
 * });
 * 
 * // PATCH
 * const updated = await apiClient.patch<Booking>('/bookings/123', {
 *   status: 'cancelled',
 * });
 * 
 * // DELETE (use void for 204 No Content)
 * const deleted = await apiClient.delete<void>('/bookings/123');
 * ```
 */
export const apiClient = {
  get,
  post,
  put,
  patch,
  delete: deleteRequest,
  request,
} as const;
